<script setup lang="ts">
import { computed, provide, reactive, ref } from "vue";
import Select from "./fields/Select.vue";
import InputMoreLessControls from "./fields/InputMoreLessControls.vue";
import { nameInjectionKey } from "./keys";

withDefaults(defineProps<{
    name?: string,
    tooltip?: string,
    modelValue?: number | null,
}>(), {
    tooltip: "",
    name: "",
    modelValue: 0,
});
type AlcoholCalculator = {
    beerABV: number,
    beerVolume: number,
    beerAmount: number,
    wineABV: number,
    wineVolume: number,
    wineAmount: number,
    sparklingWineABV: number,
    sparklingWineVolume: number,
    sparklingWineAmount: number,
    ciderABV: number,
    ciderVolume: number,
    ciderAmount: number,
    alcopopABV: number,
    alcopopVolume: number,
    alcopopAmount: number,
    spiritsABV: number,
    spiritsVolume: number,
    spiritsAmount: number,
}

const state: AlcoholCalculator = reactive({
    beerABV: 4,
    beerVolume: 568,
    beerAmount: 0,
    wineABV: 13,
    wineVolume: 175,
    wineAmount: 0,
    sparklingWineABV: 13,
    sparklingWineVolume: 175,
    sparklingWineAmount: 0,
    ciderABV: 5,
    ciderVolume: 568,
    ciderAmount: 0,
    alcopopABV: 5,
    alcopopVolume: 275,
    alcopopAmount: 0,
    spiritsABV: 40,
    spiritsVolume: 25,
    spiritsAmount: 0,
});

const emit = defineEmits<{
    (on:"update:modelValue", value: number): void
    (on:"close"): void
}>();

provide(nameInjectionKey, "AlcoholCalculator");

const totalBeer = computed(() => (state.beerVolume * state.beerAmount) * state.beerABV);
const totalWine = computed(() => (state.wineVolume * state.wineAmount) * state.wineABV);
const totalSparklingWine = computed(() => (state.sparklingWineVolume *
    state.sparklingWineAmount) * state.sparklingWineABV);
const totalCider = computed(() => (state.ciderVolume * state.ciderAmount) * state.ciderABV);
const totalAlcopop = computed(() => (state.alcopopVolume * state.alcopopAmount) * state.alcopopABV);
const totalSpirits = computed(() => (state.spiritsVolume * state.spiritsAmount) * state.spiritsABV);

const totalUnits = computed(() => Math.round((
    totalBeer.value +
    totalWine.value +
    totalCider.value +
    totalSparklingWine.value +
    totalAlcopop.value +
    totalSpirits.value) / 1000));

function GetBeerABVOptions(): (string | {value: string | number, label: string})[] {
    const selectOptions: (string | {value: string | number, label: string})[] = [];
    let strength = 3;
    for (let i = 0; i < 6; i++) {
        selectOptions[i] = { label: `${strength}%`, value: strength };
        strength = strength + 1;
    }
    return selectOptions;
}

function wineABVOptions(): (string | {value: string | number, label: string})[] {
    const selectOptions: (string | {value: string | number, label: string})[] = [];
    let strength = 11;
    for (let i = 0; i < 5; i++) {
        selectOptions[i] = { label: `${strength}%`, value: strength };
        strength = strength + 1;
    }
    return selectOptions;
}

function GetSpiritsABVOptions(): (string | {value: string | number, label: string})[] {
    const selectOptions: (string | {value: string | number, label: string})[] = [];
    let strength = 20;
    for (let i = 0; i < 10; i++) {
        selectOptions[i] = { label: `${strength}%`, value: strength };
        strength = strength + 5;
    }
    return selectOptions;
}

const pintOptions: (string | {value: string | number, label: string})[] = [
    { value: 568, label: "Pint" },
    { value: 284, label: "Half Pint" },
    { value: 660, label: "660ml" },
    { value: 440, label: "440ml" },
    { value: 330, label: "330ml" },
    { value: 275, label: "275ml" },
];

const wineOptions: (string | {value: string | number, label: string})[] = [
    { value: 750, label: "Full Bottle" },
    { value: 375, label: "Half Bottle" },
    { value: 125, label: "125ml Glass" },
    { value: 175, label: "175ml Glass" },
    { value: 250, label: "250ml Glass" },
];

const alcopopOptions: (string | {value: string | number, label: string})[] = [
    { value: 750, label: "Bottle 750ml" },
    { value: 500, label: "500ml" },
    { value: 330, label: "330ml" },
    { value: 275, label: "275ml" },
];

const spiritOptions: (string | {value: string | number, label: string})[] = [
    { value: 25, label: "25ml" },
    { value: 35, label: "35ml" },
    { value: 50, label: "50ml" },
    { value: 60, label: "60ml" },
];

const showTooltip = ref(false);

</script>
<template>
    <div class="col-md-12">
        <div class="row p-1 mb-1 mt-3">
            <div class="col-md-4" />
            <div class="col-md-2">
                <div class="row">
                    <div class="col-md-6 d-none d-md-block pe-0">
                        <span class="">ABV</span>
                    </div>
                    <div class="d-none d-md-block col-md-6 p-0">
                        <div
                            class="order-first order-md-last col-md-1"
                        >
                            <div
                                data-placement="right"
                                title="Click for help text!"
                                class="w-25"
                            >
                                <button
                                    id="alcoholCalculatorToolTip"
                                    aria-label="Click for help text"
                                    class="btn btn-circular font-weight-bold pulse help-button"
                                    type="button"
                                    aria-expanded="false"
                                    :aria-controls="name + '_collapse'"
                                    @click.prevent="showTooltip = !showTooltip"
                                >
                                    ?
                                </button>
                            </div>
                        </div>
                        <Transition mode="out-in">
                            <div
                                v-if="showTooltip"
                                :id="name+ '_collapse'"
                                class="show"
                            >
                                <div
                                    class="alert alert-warning alert-dismissible fade show mt-4 mb-4 position-absolute z-index-1"
                                    role="alert"
                                >
                                    <!--eslint-disable-next-line vue/no-v-html-->
                                    <span>Alcohol by volume (ABV) is a metric used to determine the alcohol content in an alcoholic drink. It shows what percentage of the drink’s total volume is pure alcohol. </span>
                                    <button
                                        type="button"
                                        class="btn-close"
                                        aria-label="Close"
                                        @click.prevent="showTooltip = false"
                                    />
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>
            <div class="col-md-3 d-none d-md-block">
                <span class="">Volume</span>
            </div>
            <div class="col-md-2" />
        </div>
        <div class="row d-block d-md-none">
            <div class="col-12">
                <div
                    class="alert alert-warning hideAfter"
                    role="alert"
                >
                    <span>Alcohol by volume (ABV) is a metric used to determine the alcohol content in an alcoholic drink. It shows what percentage of the drink’s total volume is pure alcohol. </span>
                </div>
            </div>
        </div>
        <fieldset class="row p-1 mb-1 mt-3 question-group">
            <div class="col-md-1 my-auto mb-2">
                <img
                    src="./../assets/images/alcohol/beer.svg"
                    alt="beer image"
                    class="img-width-2 center-object"
                >
            </div>
            <legend class="col-md-3 my-auto txt-alco">
                Beer/Lager
            </legend>
            <div class="col-md-2">
                <p class="d-block d-md-none">
                    Beer ABV
                </p>
                <label
                    for="beerABV"
                    class="visually-hidden-focusable"
                >
                    Beer ABV
                </label>
                <Select
                    id="beerABV"
                    v-model.number="state.beerABV"
                    placeholder="4%"
                    col="col-auto"
                    :options="GetBeerABVOptions()"
                />
            </div>
            <div class="col-md-3">
                <p class="d-block d-md-none">
                    Beer Volume
                </p>
                <label
                    for="beerVolume"
                    class="visually-hidden-focusable"
                >
                    Beer Volume
                </label>
                <Select
                    id="beerVolume"
                    v-model.number="state.beerVolume"
                    col="col-auto"
                    placeholder="Pint"
                    :options="pintOptions"
                />
            </div>
            <div class="ol-lg-2 col-md-3 mt-md-0 mb-4 mb-md-0">
                <p class="d-block d-md-none">
                    Beer Amount
                </p>
                <label
                    for="beerAmount"
                    class="visually-hidden-focusable"
                >
                    Beer Amount
                </label>
                <InputMoreLessControls
                    id="beerAmount"
                    v-model.number="state.beerAmount"
                />
            </div>
        </fieldset>
        <fieldset class="row p-1 mb-1 mt-3 question-group">
            <div class="col-md-1 my-auto mb-2">
                <img
                    src="./../assets/images/alcohol/wine.svg"
                    alt="wine image"
                    class="img-width-2 center-object"
                >
            </div>
            <legend class="col-md-3 my-auto txt-alco">
                Wine
            </legend>
            <div class="col-md-2">
                <p class="d-block d-md-none">
                    Wine ABV
                </p>
                <label
                    for="wineABV"
                    class="visually-hidden-focusable"
                >
                    Wine ABV
                </label>
                <Select
                    id="wineABV"
                    v-model.number="state.wineABV"
                    placeholder="13%"
                    col="col-auto"
                    :options="wineABVOptions()"
                />
            </div>
            <div class="col-md-3">
                <p class="d-block d-md-none">
                    Wine Volume
                </p>
                <label
                    for="wineVolume"
                    class="visually-hidden-focusable"
                >
                    Wine Volume
                </label>
                <Select
                    id="wineVolume"
                    v-model.number="state.wineVolume"
                    col="col-auto"
                    placeholder="175ml Glass"
                    :options="wineOptions"
                />
            </div>
            <div class="ol-lg-2 col-md-3 mt-md-0 mb-4 mb-md-0">
                <p class="d-block d-md-none">
                    Wine Amount
                </p>
                <label
                    for="wineAmount"
                    class="visually-hidden-focusable"
                >
                    Wine Amount
                </label>
                <InputMoreLessControls
                    id="wineAmount"
                    v-model.number="state.wineAmount"
                />
            </div>
        </fieldset>
        <fieldset class="row p-1 mb-1 mt-3 question-group">
            <div class="col-md-1 my-auto mb-2">
                <img
                    src="./../assets/images/alcohol/sparklingWine.svg"
                    alt="sparkling wine image"
                    class="img-width-2 center-object"
                >
            </div>
            <legend class="col-md-3 my-auto txt-alco">
                Sparkling wine
            </legend>
            <div class="col-md-2">
                <p class="d-block d-md-none">
                    Sparkling Wine ABV
                </p>
                <label
                    for="sparklingWineABV"
                    class="visually-hidden-focusable"
                >
                    Sparkling Wine ABV
                </label>
                <Select
                    id="sparklingWineABV"
                    v-model.number="state.sparklingWineABV"
                    placeholder="13%"
                    col="col-auto"
                    :options="wineABVOptions()"
                />
            </div>
            <div class="col-md-3">
                <p class="d-block d-md-none">
                    Sparkling Wine Volume
                </p>
                <label
                    for="sparklingWineVolume"
                    class="visually-hidden-focusable"
                >
                    Sparkling Wine Volume
                </label>
                <Select
                    id="sparklingWineVolume"
                    v-model.number="state.sparklingWineVolume"
                    col="col-auto"
                    placeholder="175ml Glass"
                    :options="wineOptions"
                />
            </div>
            <div class="ol-lg-2 col-md-3 mt-md-0 mb-4 mb-md-0">
                <p class="d-block d-md-none">
                    Sparkling Wine Amount
                </p>
                <label
                    for="sparklingWineAmount"
                    class="visually-hidden-focusable"
                >
                    Sparkling Wine Amount
                </label>
                <InputMoreLessControls
                    id="sparklingWineAmount"
                    v-model.number="state.sparklingWineAmount"
                />
            </div>
        </fieldset>
        <fieldset class="row p-1 mb-1 mt-3 question-group">
            <div class="col-md-1 my-auto mb-2">
                <img
                    src="./../assets/images/alcohol/cider.svg"
                    alt="cider image"
                    class="img-width-2 center-object"
                >
            </div>
            <legend class="col-md-3 my-auto txt-alco">
                Cider
            </legend>
            <div class="col-md-2">
                <p class="d-block d-md-none">
                    Cider ABV
                </p>
                <label
                    for="ciderABV"
                    class="visually-hidden-focusable"
                >
                    Cider ABV
                </label>
                <Select
                    id="ciderABV"
                    v-model.number="state.ciderABV"
                    placeholder="5%"
                    col="col-auto"
                    :options="GetBeerABVOptions()"
                />
            </div>
            <div class="col-md-3">
                <p class="d-block d-md-none">
                    Cider Volume
                </p>
                <label
                    for="ciderVolume"
                    class="visually-hidden-focusable"
                >
                    Cider Volume
                </label>
                <Select
                    id="ciderVolume"
                    v-model.number="state.ciderVolume"
                    col="col-auto"
                    placeholder="Pint"
                    :options="pintOptions"
                />
            </div>
            <div class="ol-lg-2 col-md-3 mt-md-0 mb-4 mb-md-0">
                <p class="d-block d-md-none">
                    Cider Amount
                </p>
                <label
                    for="ciderAmount"
                    class="visually-hidden-focusable"
                >
                    Cider Amount
                </label>
                <InputMoreLessControls
                    id="ciderAmount"
                    v-model.number="state.ciderAmount"
                />
            </div>
        </fieldset>
        <fieldset class="row p-1 mb-1 mt-3 question-group">
            <div class="col-md-1 my-auto mb-2">
                <img
                    src="./../assets/images/alcohol/alcopop.svg"
                    alt="alcopop image"
                    class="img-width-2 center-object"
                >
            </div>
            <legend class="col-md-3 my-auto txt-alco">
                Alcopop
            </legend>
            <div class="col-md-2">
                <p class="d-block d-md-none">
                    Alcopop ABV
                </p>
                <label
                    for="alcopopABV"
                    class="visually-hidden-focusable"
                >
                    Alcopop ABV
                </label>
                <Select
                    id="alcopopABV"
                    v-model.number="state.alcopopABV"
                    placeholder="5%"
                    col="col-auto"
                    :options="GetBeerABVOptions()"
                />
            </div>
            <div class="col-md-3">
                <p class="d-block d-md-none">
                    Alcopop Volume
                </p>
                <label
                    for="alcopopVolume"
                    class="visually-hidden-focusable"
                >
                    Alcopop Volume
                </label>
                <Select
                    id="alcopopVolume"
                    v-model.number="state.alcopopVolume"
                    col="col-auto"
                    placeholder="275ml"
                    :options="alcopopOptions"
                />
            </div>
            <div class="ol-lg-2 col-md-3 mt-md-0 mb-4 mb-md-0">
                <p class="d-block d-md-none">
                    Alcopop Amount
                </p>
                <label
                    for="alcopopAmount"
                    class="visually-hidden-focusable"
                >
                    Alcopop Amount
                </label>
                <InputMoreLessControls
                    id="alcopopAmount"
                    v-model.number="state.alcopopAmount"
                />
            </div>
        </fieldset>
        <fieldset class="row p-1 mb-1 mt-3 question-group">
            <div class="col-md-1 my-auto mb-2">
                <img
                    src="./../assets/images/alcohol/spirits.svg"
                    alt="spirits image"
                    class="img-width-2 center-object"
                >
            </div>
            <legend class="col-md-3 my-auto txt-alco">
                Spirits
            </legend>
            <div class="col-md-2">
                <p class="d-block d-md-none">
                    Spirits ABV
                </p>
                <label
                    for="spiritsABV"
                    class="visually-hidden-focusable"
                >
                    Spirits ABV
                </label>
                <Select
                    id="spiritsABV"
                    v-model.number="state.spiritsABV"
                    placeholder="40%"
                    col="col-auto"
                    :options="GetSpiritsABVOptions()"
                />
            </div>
            <div class="col-md-3">
                <p class="d-block d-md-none">
                    Spirits Volume
                </p>
                <label
                    for="spiritsVolume"
                    class="visually-hidden-focusable"
                >
                    Spirits Volume
                </label>
                <Select
                    id="spiritsVolume"
                    v-model.number="state.spiritsVolume"
                    col="col-auto"
                    placeholder="Pint"
                    :options="spiritOptions"
                />
            </div>
            <div class="ol-lg-2 col-md-3 mt-md-0 mb-4 mb-md-0">
                <p class="d-block d-md-none">
                    Spirits Amount
                </p>
                <label
                    for="spiritsAmount"
                    class="visually-hidden-focusable"
                >
                    Spirits Amount
                </label>
                <InputMoreLessControls
                    id="spiritsAmount"
                    v-model.number="state.spiritsAmount"
                />
            </div>
        </fieldset>

        <div class="row justify-content-center mt-5">
            <h5 class="text-center">
                Your estimated units per week:
            </h5>
        </div>
        <div class="row justify-content-center mb-2">
            <h1
                id="totalUnits"
                class="text-center"
            >
                {{ totalUnits }}
            </h1>
        </div>
    </div>
    <div class="text-center">
        <button
            id="alcoholCalculatorConfirm"
            type="button"
            class="btn btn-danger"
            @click="emit('update:modelValue', totalUnits); emit('close');"
        >
            Use this amount
        </button>
    </div>
</template>
